<script setup>
import {computed, onBeforeUnmount, onMounted, reactive, ref, watch} from "vue";
import {useToast} from "vue-toast-notification";
import {useAdminStore} from "@/store/admin";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";
import FormInput from "@/components/ui/FormInput.vue";

const adminStore = useAdminStore();
const toast = useToast();
const restoreTarget = ref(null);
const details = ref(null);
const saving = ref(false);
const restoreForm = reactive({password: "", confirmation: ""});
let pollTimer = null;

const STATUS_FILTER_STORAGE_KEY = "adminBackupsStatusFilter";
const filterStatus = ref(localStorage.getItem(STATUS_FILTER_STORAGE_KEY) || "");
watch(filterStatus, value => localStorage.setItem(STATUS_FILTER_STORAGE_KEY, value));
const backups = computed(() => filterStatus.value
    ? adminStore.backups.filter(item => item.status === filterStatus.value)
    : adminStore.backups);

function humanBytes(value) {
  if (value === null || value === undefined) return "—";
  const units = ["Б", "КиБ", "МиБ", "ГиБ", "ТиБ"];
  let size = Number(value);
  let index = 0;
  while (size >= 1024 && index < units.length - 1) { size /= 1024; index += 1; }
  return `${size.toFixed(index && size < 10 ? 1 : 0)} ${units[index]}`;
}

function formatDate(value) { return value ? new Date(value).toLocaleString("ru-RU") : "—"; }
function statusTitle(status) { return ({queued: "В очереди", running: "Выполняется", completed: "Готова", failed: "Ошибка", deleted: "Удалена"})[status] || status; }
function statusClass(status) { return `backupStatus backupStatus--${status || "queued"}`; }
function canRestore(item) { return item.status === "completed"; }
function hasUnknownSchema(item) { return !item?.schema_version; }

async function load(silent = false) {
  await adminStore.loadBackups(silent);
  if (adminStore.error && !silent) toast.error(adminStore.error);
}

async function create() {
  saving.value = true;
  try {
    if (!await adminStore.createBackup()) { toast.error(adminStore.error || "Не удалось создать резервную копию"); return; }
    toast.success("Создание резервной копии поставлено в очередь");
    await load(true);
  } finally { saving.value = false; }
}

async function reconcile() {
  saving.value = true;
  try {
    if (!await adminStore.reconcileBackups()) { toast.error(adminStore.error || "Не удалось запустить проверку каталога"); return; }
    toast.success("Проверка каталога резервных копий поставлена в очередь");
    await load(true);
  } finally { saving.value = false; }
}

async function verify(item) {
  if (!await adminStore.verifyBackup(item.id)) { toast.error(adminStore.error || "Не удалось запустить проверку"); return; }
  toast.success("Проверка резервной копии поставлена в очередь");
  await load(true);
}

async function deleteBackup(item) {
  if (!confirm(`Удалить резервную копию ${item.id}? Файл будет удалён без возможности восстановления.`)) return;
  if (!await adminStore.deleteBackup(item.id)) { toast.error(adminStore.error || "Не удалось поставить удаление в очередь"); return; }
  toast.success("Удаление резервной копии поставлено в очередь");
  await load(true);
}

function openRestore(item) {
  restoreTarget.value = item;
  restoreForm.password = "";
  restoreForm.confirmation = "";
}

function closeRestore() { restoreTarget.value = null; }

async function openDetails(item) {
  const result = await adminStore.loadBackupDetails(item.id);
  if (!result) { toast.error(adminStore.error || "Не удалось получить журнал"); return; }
  details.value = result;
}

function closeDetails() { details.value = null; }

async function restore() {
  const target = restoreTarget.value;
  if (!target) return;
  if (restoreForm.confirmation !== `ВОССТАНОВИТЬ ${target.id}`) {
    toast.error("Введите точную фразу подтверждения");
    return;
  }
  saving.value = true;
  try {
    const result = await adminStore.restoreBackup(target.id, {...restoreForm});
    if (!result) { toast.error(adminStore.error || "Не удалось запустить восстановление"); return; }
    toast.success("Восстановление поставлено в очередь. Сервис перейдёт в режим обслуживания.");
    closeRestore();
    await load(true);
  } finally { saving.value = false; }
}

onMounted(async () => {
  await load();
  pollTimer = window.setInterval(() => load(true), 5000);
});
onBeforeUnmount(() => { if (pollTimer) window.clearInterval(pollTimer); });
</script>

<template>
  <div class="backups">
    <MainPreloader v-if="adminStore.loading" position="fixed" :size="80" :stroke="3"/>
    <div class="backups__heading">
      <div>
        <h2>Резервные копии</h2>
        <p>Копия PostgreSQL включает данные сайта, исходные изображения и их варианты.</p>
      </div>
      <div class="backups__actions">
        <select v-model="filterStatus" class="backups__filter" aria-label="Фильтр по статусу">
          <option value="">Все статусы</option>
          <option value="queued">В очереди</option>
          <option value="running">Выполняется</option>
          <option value="completed">Готова</option>
          <option value="failed">Ошибка</option>
          <option value="deleted">Удалена</option>
        </select>
        <ActionButton :disabled="saving" @buttonClick="reconcile"><template #caption>Обновить</template></ActionButton>
        <ActionButton :disabled="saving" style="background-color: #6FC062; color: #212226;" @buttonClick="create"><template #caption>Создать копию</template></ActionButton>
      </div>
    </div>

    <div class="backups__notice">Копии не содержат секреты окружения, TLS-сертификаты и конфигурацию внешних сервисов.</div>
    <EmptyCard v-if="!backups.length && !adminStore.loading"/>
    <div v-else class="backups__tableWrap">
      <table class="backups__table">
        <thead><tr><th>Дата</th><th>Действия</th><th>Статус</th><th>Тип</th><th>Размер</th><th>Хранение</th><th>SHA-256</th></tr></thead>
        <tbody>
          <tr v-for="item in backups" :key="item.id">
            <td>{{ formatDate(item.created_at) }}</td>
            <td class="backups__rowActions">
              <button class="backups__actionButton" type="button" title="Журнал" aria-label="Открыть журнал резервной копии" @click="openDetails(item)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <button v-if="item.status === 'completed'" class="backups__actionButton" type="button" title="Проверить" aria-label="Проверить резервную копию" @click="verify(item)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="m5 12 4 4L19 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <button v-if="canRestore(item)" class="backups__actionButton backups__actionButton--restore" type="button" :title="hasUnknownSchema(item) ? 'Восстановить без проверки версии схемы' : 'Восстановить'" aria-label="Восстановить резервную копию" @click="openRestore(item)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12a9 9 0 1 0 3-6.7M3 4v6h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <button v-if="['completed', 'failed'].includes(item.status)" class="backups__actionButton backups__actionButton--danger" type="button" title="Удалить" aria-label="Удалить резервную копию" @click="deleteBackup(item)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </td>
            <td><span :class="statusClass(item.status)">{{ statusTitle(item.status) }}</span><small v-if="item.error_message">{{ item.error_message }}</small></td>
            <td>{{ item.kind === 'emergency' ? 'Аварийная' : item.kind === 'scheduled' ? 'По расписанию' : 'Ручная' }}</td>
            <td>{{ humanBytes(item.size_bytes) }}</td>
            <td>{{ item.kind === 'scheduled' ? 'Автоочистка по настройкам' : 'Без автоудаления' }}</td>
            <td><code>{{ item.sha256 ? `${item.sha256.slice(0, 12)}…` : '—' }}</code></td>
          </tr>
        </tbody>
      </table>
    </div>

    <AdminModal :visible="!!details" title="Журнал резервной копии" @close="closeDetails">
      <dl v-if="details?.data" class="backups__details">
        <dt>Идентификатор</dt><dd><code>{{ details.data.id }}</code></dd>
        <dt>SHA-256</dt><dd><code>{{ details.data.sha256 || '—' }}</code></dd>
        <dt>Версия схемы</dt><dd>{{ details.data.schema_version || '—' }}</dd>
        <dt>PostgreSQL</dt><dd>{{ details.data.postgres_version || '—' }}</dd>
      </dl>
      <ul class="backups__events">
        <li v-for="event in details?.events || []" :key="event.id" :class="`backups__event--${event.level}`">
          <time>{{ formatDate(event.created_at) }}</time><span>{{ event.message }}</span>
        </li>
        <li v-if="!(details?.events || []).length">Событий пока нет.</li>
      </ul>
      <template #footer><ActionButton @buttonClick="closeDetails"><template #caption>Закрыть</template></ActionButton></template>
    </AdminModal>

    <AdminModal :visible="!!restoreTarget" title="Восстановление резервной копии" @close="closeRestore">
      <p class="backups__warning">Текущие данные будут заменены. Перед восстановлением система создаст аварийную копию и временно закроет изменяющие операции. После успеха будут отозваны все сессии, включая текущую.</p>
      <p v-if="hasUnknownSchema(restoreTarget)" class="backups__warning">У этой найденной копии неизвестна версия схемы. Система не может автоматически проверить совместимость структуры БД. Продолжайте только если вы уверены, что дамп подходит для текущей версии приложения.</p>
      <label>Пароль текущего суперадминистратора<FormInput v-model="restoreForm.password" type="password" autocomplete="current-password"/></label>
      <label>Введите <code>ВОССТАНОВИТЬ {{ restoreTarget?.id }}</code><FormInput v-model="restoreForm.confirmation"/></label>
      <template #footer>
        <ActionButton @buttonClick="closeRestore"><template #caption>Отмена</template></ActionButton>
        <ActionButton :disabled="saving" style="background-color: var(--cd-danger); color: white;" @buttonClick="restore"><template #caption>Восстановить</template></ActionButton>
      </template>
    </AdminModal>
  </div>
</template>

<style scoped lang="scss">
.backups { display: flex; flex-direction: column; gap: 18px;
  &__heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; h2 { margin: 0; font-size: 20px; } p { margin: 6px 0 0; color: var(--cd-text-muted); } }
  &__actions, &__rowActions { display: flex; gap: 8px; align-items: center; }
  &__filter { height: 40px; border-radius: 20px; padding: 5px 20px; background: var(--cd-field-bg); color: var(--cd-field-color); border: 1px solid var(--cd-field-border); font-size: 14px; font-family: var(--cd-default-font); cursor: pointer; &:focus { outline: none; border-color: var(--cd-base-green); box-shadow: 0 0 5px rgba(98, 192, 100, .3); } }
  &__notice, &__warning { padding: 14px 16px; border-radius: 12px; background: rgba(111, 192, 98, .12); color: var(--cd-text); }
  &__warning { background: rgba(210, 80, 80, .14); margin: 0; }
  &__tableWrap { overflow-x: auto; }
  &__table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 8px;
    text-align: left;
    min-width: 780px;

    th {
      color: var(--cd-text-muted);
      font-size: 12px;
      font-weight: 400;
      padding: 0 16px 4px;
    }

    td {
      background: var(--cd-card-bg);
      padding: 12px 16px;
      font-size: 14px;
      color: var(--cd-text);
      vertical-align: middle;

      &:first-child { border-radius: 12px 0 0 12px; }
      &:last-child { border-radius: 0 12px 12px 0; }
    }

    small { display: block; max-width: 220px; color: var(--cd-danger); margin-top: 5px; }
  }
  &__rowActions button { padding: 7px 10px; border: 1px solid var(--cd-field-border); border-radius: 8px; background: transparent; color: var(--cd-text); cursor: pointer; &:hover { border-color: var(--cd-base-green); } }
  &__actionButton { padding: 6px !important; display: inline-flex; align-items: center; justify-content: center; border-color: var(--cd-base-green) !important; color: var(--cd-base-green) !important; transition: all .3s; &:hover { background: var(--cd-base-green) !important; color: white !important; } &--danger, &--restore { border-color: var(--cd-danger) !important; color: var(--cd-danger) !important; &:hover { background: var(--cd-danger) !important; color: white !important; } } }
  &__details { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 8px 12px; margin: 0 0 16px; dt { color: var(--cd-text-muted); } dd { margin: 0; overflow-wrap: anywhere; } }
  &__events { display: flex; flex-direction: column; gap: 8px; padding: 0; margin: 0; list-style: none; li { display: flex; flex-direction: column; gap: 2px; padding: 10px; border-left: 3px solid var(--cd-field-border); background: var(--cd-card-bg); } time { color: var(--cd-text-muted); font-size: 12px; } }
  &__event--error { border-left-color: var(--cd-danger) !important; }
  label { display: flex; flex-direction: column; gap: 6px; color: var(--cd-text); }
}
.backupStatus { font-size: 12px;
  &--completed { color: #276d1d; }
  &--failed { color: #9f2727; }
  &--running { color: #8c6400; }
}
@media (max-width: 700px) { .backups__heading { align-items: flex-start; flex-direction: column; } }
</style>
